import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import Card from '@/components/ui/Card'
import { api } from '@/services/api'

const DOCUMENT_TITLES: Record<string, string> = {
  'terms-of-use': 'Terms of Use',
  'privacy-policy': 'Privacy Policy',
  'barter-agreement': 'Barter Agreement',
  'escrow-policy': 'Escrow Policy',
  'rating-reviews-policy': 'Rating & Reviews Policy',
}

export default function LegalDocument() {
  const { documentType } = useParams<{ documentType: string }>()
  const [content, setContent] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    const fetchDocument = async () => {
      if (!documentType) return

      try {
        setLoading(true)
        const response = await api.get<{ content: string }>(`/legal/${documentType}`)

        // The API client already returns response.data, so we access data.content directly
        if (response.data && response.data.content) {
          setContent(response.data.content)
        } else {
          setError('Document content not found in response')
        }
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load document')
      } finally {
        setLoading(false)
      }
    }

    fetchDocument()
  }, [documentType])

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto py-12 px-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-4 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto py-12 px-4">
        <Card>
          <div className="text-center py-8">
            <p className="text-red-600 mb-4">{error}</p>
            <Link to="/" className="text-primary-600 hover:text-primary-500">
              Return Home
            </Link>
          </div>
        </Card>
      </div>
    )
  }

  const title = documentType ? DOCUMENT_TITLES[documentType] : 'Legal Document'

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/"
            className="text-primary-600 hover:text-primary-500 text-sm font-medium flex items-center"
          >
            ← Back to Home
          </Link>
        </div>

        <Card>
          <div className="p-6">
            {/* Document Title */}
            <h1 className="text-3xl font-bold text-gray-900 mb-6">{title}</h1>

            {/* Markdown Content */}
            <div className="prose prose-sm max-w-none">
              <ReactMarkdown
                components={{
                  h1: ({ node, ...props }) => (
                    <h1 className="text-2xl font-bold mt-6 mb-4 text-gray-900" {...props} />
                  ),
                  h2: ({ node, ...props }) => (
                    <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900" {...props} />
                  ),
                  h3: ({ node, ...props }) => (
                    <h3 className="text-lg font-semibold mt-4 mb-2 text-gray-900" {...props} />
                  ),
                  p: ({ node, ...props }) => <p className="mb-4 text-gray-700 leading-relaxed" {...props} />,
                  ul: ({ node, ...props }) => <ul className="list-disc pl-6 mb-4 space-y-2" {...props} />,
                  ol: ({ node, ...props }) => <ol className="list-decimal pl-6 mb-4 space-y-2" {...props} />,
                  li: ({ node, ...props }) => <li className="text-gray-700" {...props} />,
                  strong: ({ node, ...props }) => <strong className="font-semibold text-gray-900" {...props} />,
                  a: ({ node, ...props }) => (
                    <a
                      className="text-primary-600 hover:text-primary-500 underline"
                      target="_blank"
                      rel="noopener noreferrer"
                      {...props}
                    />
                  ),
                  hr: ({ node, ...props }) => <hr className="my-8 border-gray-200" {...props} />,
                  blockquote: ({ node, ...props }) => (
                    <blockquote
                      className="border-l-4 border-primary-500 pl-4 py-2 my-4 bg-gray-50 italic"
                      {...props}
                    />
                  ),
                }}
              >
                {content}
              </ReactMarkdown>
            </div>
          </div>
        </Card>

        {/* Footer Navigation */}
        <div className="mt-8 border-t pt-6">
          <h3 className="text-lg font-semibold mb-4">Other Legal Documents</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(DOCUMENT_TITLES).map(([type, label]) => (
              <Link
                key={type}
                to={`/legal/${type}`}
                className={`px-4 py-3 rounded-lg border transition-colors ${
                  type === documentType
                    ? 'bg-primary-50 border-primary-300 text-primary-700 font-medium'
                    : 'bg-white border-gray-200 hover:border-primary-300 text-gray-700 hover:text-primary-600'
                }`}
              >
                {label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
